/**
 * 经营分析六页共用的部件：图表容器、指标说明角标、可排序表头、CSV 导出、安全的 Markdown 渲染与格式化小函数。
 *
 * 背景：Alice 的经营分析页（驾驶舱、仪表盘、付费分析、成本测算、用量统计）都是同一套交互：chart.js 图表、
 *   表头带「i」说明角标、点表头排序、导出 CSV。Alice 用全局函数与 innerHTML 实现，这里改成 React 组件，类名不变。
 * 设计意图：
 *   - 图表只在 config 变化时重建（页面里用 useMemo 产出 config），卸载时 destroy，避免泄漏与重复画布。
 *   - 排序在前端对已加载的整批数据做，规则与 Alice 一致：空值永远排最后，数字按数值、文本按本地化比较。
 *   - Markdown 只支持报告里用到的子集（标题、列表、表格、粗体、行内代码、代码块、引用），全程生成 React 节点，
 *     不用 innerHTML，导入的报告内容不会变成脚本。
 * 关键约束：不改 kit.tsx 的既有导出；新增的类名都已在 alice-admin.css 里。
 */
import { Chart, registerables, type ChartConfiguration, type ChartOptions } from 'chart.js'
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { translate, type Entry, type Locale } from '@ap/i18n'
import { BIZ } from '@ap/i18n/admin-biz'
import { errorText } from '@ap/i18n/common'

Chart.register(...registerables)

// ------------------------------------------------------------------ 图表

/** Alice 的 _chartOpts：图例靠右上、轴线隐藏、Y 轴从 0 起、悬停按 x 对齐。 */
export function chartOpts(): ChartOptions & { scales: Record<string, Record<string, unknown>>; plugins: Record<string, Record<string, unknown>> } {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', align: 'end', labels: { font: { size: 10 }, usePointStyle: true, pointStyle: 'circle', boxWidth: 6 } },
      title: { display: false },
    },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 10 }, color: '#9ca3af' } },
      y: { beginAtZero: true, border: { display: false }, grid: { color: 'rgba(148,163,184,0.12)' }, ticks: { font: { size: 10 }, color: '#9ca3af' } },
    },
    interaction: { intersect: false, mode: 'index' },
  } as never
}

/** 画布：父容器用 chart-h-* 类定高度，config 变化才重建。 */
export function ChartCanvas({ config, label }: { config: ChartConfiguration; label: string }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    if (!ref.current) return
    const chart = new Chart(ref.current, config)
    return () => chart.destroy()
  }, [config])
  return <canvas ref={ref} role="img" aria-label={label} />
}

/** 堆叠图的 14 色色板（Alice _OV_STACK_PALETTE）：按区间总额名次取色，最贵的永远同色。 */
export const STACK_PALETTE = [
  'rgba(220,38,38,0.75)', 'rgba(234,88,12,0.75)', 'rgba(217,119,6,0.75)',
  'rgba(202,138,4,0.75)', 'rgba(101,163,13,0.75)', 'rgba(22,163,74,0.75)',
  'rgba(13,148,136,0.75)', 'rgba(8,145,178,0.75)', 'rgba(37,99,235,0.75)',
  'rgba(79,70,229,0.75)', 'rgba(124,58,237,0.75)', 'rgba(192,38,211,0.75)',
  'rgba(219,39,119,0.75)', 'rgba(100,116,139,0.75)',
]

/**
 * 堆叠数据集：只画总额最高的 topN 类，其余合并成「其他 N 项」，避免二十几种颜色谁也看不清。
 * valueOf 把后端的 unit 换成图上的数值（美元）。
 */
export function stackDatasets(
  summary: { key: string }[],
  daily: Record<string, Record<string, number>>,
  dates: string[],
  opts: { topN?: number; nameOf?: (k: string) => string; otherLabel: (n: number) => string; valueOf: (units: number) => number; stack?: string },
) {
  const topN = opts.topN ?? 12
  const stack = opts.stack ?? 'cost'
  const top = summary.slice(0, topN)
  const rest = summary.slice(topN)
  const at = (date: string, key: string) => opts.valueOf((daily[date] ?? {})[key] ?? 0)
  const sets = top.map((row, i) => ({
    label: opts.nameOf ? opts.nameOf(row.key) : row.key,
    data: dates.map((d) => at(d, row.key)),
    backgroundColor: STACK_PALETTE[i % STACK_PALETTE.length],
    stack,
    borderRadius: 0,
  }))
  if (rest.length) {
    sets.push({
      label: opts.otherLabel(rest.length),
      data: dates.map((d) => rest.reduce((s, r) => s + at(d, r.key), 0)),
      backgroundColor: 'rgba(148,163,184,0.6)',
      stack,
      borderRadius: 0,
    })
  }
  if (sets.length) sets[sets.length - 1].borderRadius = 4
  return sets
}

// ------------------------------------------------------------------ 指标说明角标

/**
 * 表头与卡片标题旁的「i」：悬停或聚焦弹出摘要，点击或回车打开完整说明（Alice showCostMetricHelp）。
 * 说明文案 [标题, 正文] 由页面传入，三语在 admin-biz.ts 的 help 表里。
 */
export function MetricInfo({ title, body, closeLabel }: { title: string; body: string; closeLabel: string }) {
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)
  const place = (x: number, y: number) => setTip({ x: x + 12, y: y + 12 })
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])
  return (
    <>
      <span
        ref={ref}
        className="metric-info"
        role="button"
        tabIndex={0}
        aria-label={title}
        onMouseEnter={(e) => place(e.clientX, e.clientY)}
        onMouseMove={(e) => place(e.clientX, e.clientY)}
        onMouseLeave={() => setTip(null)}
        onFocus={() => {
          const r = ref.current?.getBoundingClientRect()
          if (r) place(r.left + r.width / 2, r.bottom)
        }}
        onBlur={() => setTip(null)}
        onClick={(e) => {
          e.stopPropagation()
          setTip(null)
          setOpen(true)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.stopPropagation()
            setTip(null)
            setOpen(true)
          }
        }}
      >
        i
      </span>
      {tip &&
        createPortal(
          <div id="caMetricHover" role="tooltip" className="visible" style={{ left: Math.min(tip.x, window.innerWidth - 392), top: tip.y }}>
            <strong>{title}</strong>
            {body}
          </div>,
          document.body,
        )}
      {open &&
        createPortal(
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, backdropFilter: 'blur(3px)' }}
            onClick={() => setOpen(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label={title}
              style={{ width: 460, maxWidth: '94vw', background: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: 12, padding: 20, boxShadow: '0 24px 60px rgba(15,23,42,.24)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 10 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
                <button className="btn btn-secondary btn-sm" onClick={() => setOpen(false)} aria-label={closeLabel}>
                  ✕
                </button>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--text-secondary)' }}>{body}</div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

/** 按 help 表的 key 取说明并渲染角标。 */
export function Help({ k, help, locale, closeLabel }: { k: string; help: Record<string, { title: Entry; body: Entry }>; locale: Locale; closeLabel: string }) {
  const h = help[k]
  if (!h) return null
  return <MetricInfo title={translate(h.title, locale)} body={translate(h.body, locale)} closeLabel={closeLabel} />
}

// ------------------------------------------------------------------ 排序

export type SortDir = 'asc' | 'desc'
export interface SortState {
  key: string
  dir: SortDir
}

function sortValue(row: Record<string, unknown>, key: string): number | string | null {
  const v = row[key]
  if (v === null || v === undefined || v === '') return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const s = String(v).trim()
  return /^-?\d+(\.\d+)?$/.test(s) ? Number(s) : s
}

/** Alice _sortedRows：空值永远排最后，数字按数值，文本按本地化比较。 */
export function useSorted<T extends object>(rows: T[], initial: SortState, locale: string) {
  const [state, setState] = useState<SortState>(initial)
  const sorted = useMemo(() => {
    const copy = rows.slice()
    copy.sort((l, r) => {
      const a = sortValue(l as Record<string, unknown>, state.key)
      const b = sortValue(r as Record<string, unknown>, state.key)
      if (a === null && b === null) return 0
      if (a === null) return 1
      if (b === null) return -1
      const cmp = typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b), locale)
      return state.dir === 'asc' ? cmp : -cmp
    })
    return copy
  }, [rows, state, locale])
  const toggle = (key: string) => setState((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))
  return { sorted, state, toggle }
}

/** 可排序表头：点击切换方向，说明角标（children）点击不触发排序。 */
export function SortTh({
  sortKey,
  state,
  onSort,
  children,
  help,
}: {
  sortKey: string
  state: SortState
  onSort: (key: string) => void
  children: ReactNode
  help?: ReactNode
}) {
  const active = state.key === sortKey
  return (
    <th
      className={`sortable-th${active ? ' sorted' : ''}`}
      data-sort-dir={active ? state.dir : ''}
      onClick={() => onSort(sortKey)}
      aria-sort={active ? (state.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {children}
      {help}
    </th>
  )
}

// ------------------------------------------------------------------ CSV

/** Alice exportCaCsv：UTF-8 BOM 加双引号转义，文件名带日期范围，Excel 直接打开不乱码。 */
export function downloadCsv(filename: string, cols: { key: string; label: string }[], rows: Record<string, unknown>[]) {
  const cell = (v: unknown) => {
    if (v === null || v === undefined) return ''
    const s = String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const lines = [cols.map((c) => cell(c.label)).join(','), ...rows.map((r) => cols.map((c) => cell(r[c.key])).join(','))]
  const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(a.href), 1000)
}

// ------------------------------------------------------------------ 表单控件样式

/** Alice 筛选栏里 select、date 输入的内联样式（原样搬过来，保持两边外观一致）。 */
export const FIELD: CSSProperties = {
  padding: '7px 10px',
  fontSize: 13,
  background: 'var(--bg-input)',
  border: '1px solid var(--border-medium)',
  borderRadius: 6,
  color: 'var(--text-primary)',
  fontFamily: 'var(--font)',
}

export const NUM_FIELD: CSSProperties = { ...FIELD, padding: '6px 8px', fontSize: 13 }

// ------------------------------------------------------------------ 错误

/**
 * 经营分析接口的错误提示：先查本组新增错误码（日期范围、改写依赖渠道等），其余沿用共用错误表。
 * invalid_request 的原因只有服务端知道（哪一项规则不合法），附在文案后面方便运营定位。
 */
export function bizError(err: unknown, locale: Locale): string {
  const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : ''
  const entry = BIZ.errors[code as keyof typeof BIZ.errors] as Entry | undefined
  if (!entry) return errorText(err, locale)
  const detail = code === 'invalid_request' && err instanceof Error && err.message ? `: ${err.message}` : ''
  return translate(entry, locale) + detail
}

// ------------------------------------------------------------------ 日期与格式

/** 北京时间自然日（业务时区），offset 为相对今天的天数。 */
export function beijingDate(offsetDays = 0): string {
  return new Date(Date.now() + offsetDays * 86400000).toLocaleDateString('sv-SE', { timeZone: 'Asia/Shanghai' })
}

export function spanDays(start: string, end: string): number {
  return Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86400000) + 1
}

export function pctOf(part: number, total: number): string {
  return total > 0 ? `${((part / total) * 100).toFixed(1)}%` : '—'
}

export function ratePct(rate: number | null | undefined): string {
  return rate === null || rate === undefined ? '—' : `${(rate * 100).toFixed(1)}%`
}

/** Token 紧凑显示（Alice _fmtTokens）：K、M、B。 */
export function compactTokens(n: number): string {
  const v = Math.abs(n)
  if (v >= 1e9) return `${(n / 1e9).toFixed(2)}B`
  if (v >= 1e6) return `${(n / 1e6).toFixed(2)}M`
  if (v >= 1e3) return `${(n / 1e3).toFixed(1)}K`
  return String(Math.round(n))
}

export function growthText(v: number | null | undefined, none: string): string {
  if (v === null || v === undefined) return none
  return `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`
}

export function runwayText(v: number | null | undefined, unit: (n: string) => string): string {
  if (v === null || v === undefined) return '—'
  return v >= 999 ? unit('999+') : unit(v.toFixed(1))
}

/** 成功绿、警告黄、错误红三档（覆盖率）。 */
export function coverageColor(rate: number): string {
  return rate >= 0.95 ? 'var(--success-text)' : rate >= 0.7 ? 'var(--warning-text)' : 'var(--error-text)'
}

export function signColor(n: number): string {
  return n >= 0 ? 'var(--success-text)' : 'var(--error-text)'
}

// ------------------------------------------------------------------ Markdown

function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = []
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const tok = m[0]
    out.push(tok.startsWith('**') ? <strong key={`${key}-${i}`}>{tok.slice(2, -2)}</strong> : <code key={`${key}-${i}`}>{tok.slice(1, -1)}</code>)
    last = m.index + tok.length
    i++
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** 报告正文渲染：标题、有序与无序列表、管道表格、引用、代码块、段落。 */
export function Markdown({ text }: { text: string }) {
  const blocks: ReactNode[] = []
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  let i = 0
  let n = 0
  const k = () => `b${n++}`
  while (i < lines.length) {
    const line = lines[i]
    if (!line.trim()) {
      i++
      continue
    }
    const fence = line.match(/^```/)
    if (fence) {
      const code: string[] = []
      i++
      while (i < lines.length && !lines[i].startsWith('```')) code.push(lines[i++])
      i++
      blocks.push(<pre key={k()}>{code.join('\n')}</pre>)
      continue
    }
    const h = line.match(/^(#{1,3})\s+(.*)$/)
    if (h) {
      const Tag = (`h${h[1].length}` as 'h1' | 'h2' | 'h3')
      blocks.push(<Tag key={k()}>{inline(h[2], `h${n}`)}</Tag>)
      i++
      continue
    }
    if (line.includes('|') && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      const cells = (s: string) => s.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim())
      const head = cells(line)
      i += 2
      const body: string[][] = []
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) body.push(cells(lines[i++]))
      blocks.push(
        <table key={k()}>
          <thead>
            <tr>{head.map((c, j) => <th key={j}>{inline(c, `th${n}-${j}`)}</th>)}</tr>
          </thead>
          <tbody>
            {body.map((r, j) => (
              <tr key={j}>{r.map((c, x) => <td key={x}>{inline(c, `td${n}-${j}-${x}`)}</td>)}</tr>
            ))}
          </tbody>
        </table>,
      )
      continue
    }
    if (/^\s*[-*]\s+/.test(line) || /^\s*\d+[.)]\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]\s+/.test(line)
      const items: string[] = []
      while (i < lines.length && (ordered ? /^\s*\d+[.)]\s+/ : /^\s*[-*]\s+/).test(lines[i])) {
        items.push(lines[i].replace(ordered ? /^\s*\d+[.)]\s+/ : /^\s*[-*]\s+/, ''))
        i++
      }
      const Tag = ordered ? 'ol' : 'ul'
      blocks.push(<Tag key={k()}>{items.map((it, j) => <li key={j}>{inline(it, `li${n}-${j}`)}</li>)}</Tag>)
      continue
    }
    if (line.startsWith('>')) {
      const q: string[] = []
      while (i < lines.length && lines[i].startsWith('>')) q.push(lines[i++].replace(/^>\s?/, ''))
      blocks.push(<blockquote key={k()}>{inline(q.join(' '), `q${n}`)}</blockquote>)
      continue
    }
    const para: string[] = []
    while (i < lines.length && lines[i].trim() && !/^(#{1,3}\s|```|>|\s*[-*]\s+|\s*\d+[.)]\s+)/.test(lines[i])) para.push(lines[i++])
    blocks.push(<p key={k()}>{inline(para.join(' '), `p${n}`)}</p>)
  }
  return <div className="md-body">{blocks}</div>
}
